import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Option,
    Select,
} from '@mui/joy'
import { useQrCodeCheck, useQrCodeFirstCall } from 'api/chat-api-integration'
import { ChatApiItem } from 'components/ChatApisPreview/ChatApisPreview'
import { useQuery, useQueryClient } from 'react-query'
import QRCode from 'react-qr-code'
import qrTemplateImage from 'assets/images/qr-template.png'
import { useContext, useState } from 'react'
import { AuthContext } from 'context/Auth'

type Props = {
    item: ChatApiItem
    setPopup: React.Dispatch<React.SetStateAction<ChatApiItem | null>>
    setResponseError: React.Dispatch<React.SetStateAction<string | null>>
}

const WhatsAppIntegration = ({ item, setPopup, setResponseError }: Props) => {
    const queryClient = useQueryClient()

    const { user } = useContext(AuthContext)

    if (!user?.access) {
        throw new Error('User has no access and cannot fetch messages.')
    }

    const workspaceId = user?.access?.workspaceId
    const userId = user?.access?.internalUserId
    const userEmail = user.email

    const [archiveDepth, setArchiveDepth] = useState<number>(92)

    const {
        mutate: generateQr,
        data,
        isLoading,
        isError,
        isSuccess,
    } = useQrCodeFirstCall('whatsapp')

    const qrCode = data?.qrCode
    const objectId = data?.objectId

    const qrCodeCheck = useQrCodeCheck(
        'whatsapp',
        {
            type: 4,
            workspaceId: workspaceId,
            userId: userId,
            qrcodeOptions: {
                archiveDepth: archiveDepth,
                email: userEmail,
                objectId,
                prevQrCodeId: qrCode?.id,
            },
            dataEnrichmentOptions: {
                enrichmentRulesGroups: ['basic'],
            },
        },
        {
            enabled: !!qrCode && !!objectId,
            refetchInterval: (data) => {
                return data?.status === 'Authorized' ? false : 20 * 1000
            },
            onSuccess: (data: any) => {
                if (data?.status === 'Authorized') {
                    setPopup(null)
                    queryClient.invalidateQueries(['integrationList'])
                }

                if (data?.status === 'DuplicateIdentified') {
                    setPopup(null)
                    setResponseError(data?.error)
                    return
                }
            },
            onError: (data: any) => {
                setPopup(null)
                setResponseError(data?.error)
            },
        }
    )

    // console.log(qrCodeCheck, 'qrCodeCheck')

    if (isError) return <p>Something went wrong</p>

    const handleArchiveDepth = (
        event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent | null,
        value: number | null
    ) => {
        setArchiveDepth(value ?? 0)
    }

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 3,
            }}
        >
            <Box sx={{}}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                    }}
                >
                    <img src={item.icon} alt={item.label} />
                    <Typography level="title-md">{item?.label}</Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Typography level="body-md" sx={{ mt: 1 }}>
                            1. Select archive depth
                        </Typography>
                        <Select
                            placeholder="Choose one…"
                            variant="outlined"
                            defaultValue={92}
                            sx={{
                                width: 'fit-content',
                            }}
                            onChange={handleArchiveDepth}
                        >
                            <Option value={0}>None</Option>
                            <Option value={31}>1 month</Option>
                            <Option value={92}>3 months</Option>
                            <Option value={365}>12 months</Option>
                        </Select>
                    </Box>

                    <Typography level="body-md" sx={{ mt: 1 }}>
                        2. Open {item.label} on your phone
                    </Typography>
                    <Typography level="body-md" sx={{ mt: 1 }}>
                        3. Tap{' '}
                        <Typography
                            component="span"
                            fontWeight="lg"
                            sx={(theme) => ({
                                color: 'white',
                                [theme.getColorSchemeSelector('light')]: {
                                    color: '#000',
                                },
                            })}
                        >
                            Menu
                        </Typography>{' '}
                        or{' '}
                        <Typography
                            component="span"
                            fontWeight="lg"
                            sx={(theme) => ({
                                color: 'white',
                                [theme.getColorSchemeSelector('light')]: {
                                    color: '#000',
                                },
                            })}
                        >
                            Settings
                        </Typography>{' '}
                        and select{' '}
                        <Typography
                            component="span"
                            fontWeight="lg"
                            sx={(theme) => ({
                                color: 'white',
                                [theme.getColorSchemeSelector('light')]: {
                                    color: '#000',
                                },
                            })}
                        >
                            Linked Devices
                        </Typography>
                    </Typography>
                    <Typography level="body-md" sx={{ mt: 1 }}>
                        4. Point your phone to this screen to capture the code
                    </Typography>
                </Box>
            </Box>
            <Box>
                {!isSuccess && (
                    <Box
                        sx={{
                            position: 'relative',
                        }}
                    >
                        <img
                            src={qrTemplateImage}
                            width={250}
                            height={250}
                            alt="QR"
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: 0,
                                right: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backdropFilter: 'blur(4px)',
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            {isLoading ? (
                                <>
                                    <CircularProgress
                                        color="primary"
                                        size="lg"
                                    />
                                </>
                            ) : (
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={() =>
                                        generateQr({
                                            type: 4,
                                            workspaceId: workspaceId,
                                            userId: userId,
                                            qrcodeOptions: {
                                                archiveDepth: archiveDepth,
                                                email: userEmail,
                                                objectId: null,
                                            },
                                            dataEnrichmentOptions: {
                                                enrichmentRulesGroups: [
                                                    'basic',
                                                ],
                                            },
                                        })
                                    }
                                >
                                    Generate QR Code
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}

                {qrCode?.data && (
                    <img
                        src={qrCode.data}
                        alt="WhatsApp QR"
                        width={250}
                        height={250}
                    />
                )}
            </Box>
        </Box>
    )
}

export default WhatsAppIntegration
